// src/components/subscription/PaymentCancel.jsx (enhanced version)
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  XCircle, 
  ArrowRight, 
  RefreshCw, 
  Home, 
  AlertTriangle,
  Clock,
  CreditCard,
  CheckCircle
} from 'lucide-react';
import { PaymentService } from '../../services/paymentService';
import { useAuth } from '../../contexts/AuthContext';

const PaymentCancel = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [processing, setProcessing] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);
  const [cancellationProcessed, setCancellationProcessed] = useState(false);

  useEffect(() => {
    handlePaymentCancel();
  }, []);

  const handlePaymentCancel = async () => {
    try {
      const params = Object.fromEntries(searchParams);
      const subscriptionId = params.m_payment_id;
      
      if (subscriptionId && currentUser) {
        // Process the cancellation
        await PaymentService.handlePaymentCancel(subscriptionId, currentUser.uid);
        setCancellationProcessed(true);
        setSubscriptionData({ subscriptionId });
      } else if (subscriptionId) {
        // Just store the subscription ID if no user
        setSubscriptionData({ subscriptionId });
      }
    } catch (error) {
      console.error('Error handling payment cancellation:', error);
      setError('Error processing cancellation');
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryPayment = () => {
    navigate('/pricing');
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const getEarlyBirdCountdown = () => {
    const earlyBirdEnd = new Date('2025-06-30');
    const now = new Date();
    const timeDiff = earlyBirdEnd - now;
    
    if (timeDiff <= 0) return null;
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return { days, hours };
  };

  const countdown = getEarlyBirdCountdown();

  if (processing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6e55a3] mx-auto mb-4"></div>
          <p className="text-gray-600">Processing cancellation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {/* Cancellation Icon */}
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-orange-600" />
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Cancelled</h1>
          
          {/* Description */}
          <p className="text-gray-600 mb-6">
            Your payment was cancelled and no charges were made to your account. 
            {cancellationProcessed && " We've updated your subscription status accordingly."}
          </p>

          {/* Cancellation Confirmation */}
          {cancellationProcessed && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Cancellation processed successfully</span>
              </div>
            </div>
          )}
          
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <h3 className="text-sm font-medium text-blue-800 mb-1">What happens next?</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• No payment was processed</li>
                  <li>• Your subscription was not activated</li>
                  <li>• You can retry payment anytime</li>
                  <li>• Your data remains safe and secure</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Early Bird Reminder */}
          {countdown && (
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-3 mb-6">
              <div className="flex items-center justify-center gap-2 text-orange-800 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Early Bird Special Still Available!</span>
              </div>
              <p className="text-xs text-orange-700 mb-1">
                50% off annual plans ends in:
              </p>
              <p className="text-sm font-bold text-orange-900">
                {countdown.days} days, {countdown.hours} hours
              </p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleRetryPayment}
              className="w-full bg-[#6e55a3] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#6156a6] transition-colors flex items-center justify-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              Try Payment Again
            </button>
            
            <button
              onClick={handleGoHome}
              className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <Home className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Contact Support */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">
              Need help with your subscription?
            </p>
            <a 
              href="mailto:support@taskregister.co.za" 
              className="text-[#6e55a3] hover:text-[#6156a6] text-sm font-medium"
            >
              Contact Support
            </a>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <h3 className="font-medium text-gray-900 mb-3 text-center">Why choose TaskRegister?</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#6e55a3] rounded-full"></div>
              <span>AI-powered performance analytics</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#6e55a3] rounded-full"></div>
              <span>Streamlined assessment management</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#6e55a3] rounded-full"></div>
              <span>Secure PayFast payment processing</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#6e55a3] rounded-full"></div>
              <span>14-day money-back guarantee</span>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-6 bg-gray-100 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3 text-center">Frequently Asked Questions</h3>
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium text-gray-800">Will I be charged for the cancelled payment?</p>
              <p className="text-gray-600">No, no charges were made to your account.</p>
            </div>
            <div>
              <p className="font-medium text-gray-800">Can I still get the early bird discount?</p>
              <p className="text-gray-600">Yes, the early bird pricing is still available until June 30, 2025.</p>
            </div>
            <div>
              <p className="font-medium text-gray-800">Is my data safe?</p>
              <p className="text-gray-600">Absolutely. Your account and data remain secure and unchanged.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancel;